import { signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AppMessagesComponent } from '@common/messages/frontend-shared';
import { UiButtonComponent } from '@common/ui/frontend-shared/button';
import { UiContentCenteredComponent } from '@common/ui/frontend-shared/content-centered';
import { UiIconsModule } from '@common/ui/frontend-shared/icons';
import { AccessFacade, AccessState, AppRequestService, LocalMessageHandlerService } from '@core/frontend-shared';
import { StartSessionByLinkRequest } from '@core/shared/access';
import { Store } from '@ngxs/store';
import { take } from 'rxjs/operators';
import * as i0 from "@angular/core";
import * as i1 from "@angular/router";
import * as i2 from "@core/frontend-shared";
import * as i3 from "@ngxs/store";
import * as i4 from "@common/ui/frontend-shared/card";
import * as i5 from "../../../../../ui/frontend-shared/src/lib/icons/icon.component";
function CoreBeginSessionByUrlComponent_Conditional_2_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 3);
    i0.ɵɵelement(1, "ui-icon", 4);
    i0.ɵɵelementEnd();
    i0.ɵɵelementStart(2, "div", 5)(3, "h2");
    i0.ɵɵtext(4, "Login wird durchgef\u00FChrt...");
    i0.ɵɵelementEnd()();
  }
  if (rf & 2) {
    i0.ɵɵadvance();
    i0.ɵɵproperty("spin", true);
  }
}
function CoreBeginSessionByUrlComponent_Conditional_2_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "button", 2);
  }
}
function CoreBeginSessionByUrlComponent_Conditional_2_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 1);
    i0.ɵɵconditionalCreate(1, CoreBeginSessionByUrlComponent_Conditional_2_Conditional_1_Template, 5, 1);
    i0.ɵɵelement(2, "app-messages");
    i0.ɵɵconditionalCreate(3, CoreBeginSessionByUrlComponent_Conditional_2_Conditional_3_Template, 1, 0, "button", 2);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵadvance();
    i0.ɵɵconditional(ctx_r0.isLoading() ? 1 : -1);
    i0.ɵɵadvance(2);
    i0.ɵɵconditional(!ctx_r0.isLoading() ? 3 : -1);
  }
}
function CoreBeginSessionByUrlComponent_Conditional_3_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "p");
    i0.ɵɵtext(1, "Der Login via URL ist auf dieser Plattform nicht erlaubt.");
    i0.ɵɵelementEnd();
    i0.ɵɵelement(2, "button", 2);
  }
}
/**
 * Important: We cannot rely on Observables in here.
 * When login status changes, this component will be recreated.
 * This will create a code loop.
 * So we need to do a redirect to another route to break the loop.
 */
export let CoreBeginSessionByUrlComponent = /*#__PURE__*/(() => {
  class CoreBeginSessionByUrlComponent {
    constructor(route, router, facade, store, messages, api) {
      this.route = route;
      this.router = router;
      this.facade = facade;
      this.store = store;
      this.messages = messages;
      this.api = api;
      this.isFrontend = PLATFORM === 'frontend';
      this.isLoading = signal(true, ...(ngDevMode ? [{
        debugName: "isLoading"
      }] : /* istanbul ignore next */[]));
    }
    ngOnInit() {
      if (this.isFrontend) this.tryLogin();
    }
    tryLogin() {
      const alreadyLoggedIn = this.store.selectSnapshot(AccessState.loggedIn);
      if (alreadyLoggedIn) {
        // if user is currently logged in, we need to logout and do a page reload.
        // this is to make sure no data of the other account is still cached/displayed.
        this.facade.logout();
      }
      const sessionId = this.route.snapshot.queryParams['sessionId'] || this.route.snapshot.queryParams['token'];
      const loginByLinkSecret = this.route.snapshot.queryParams['loginByLinkSecret'];
      if (!sessionId || !loginByLinkSecret) {
        this.messages.create().setSeverity('warn').setMessage('Dieser Link ist ungültig. Bitte klicken Sie erneut den Link in der Mail oder lassen Sie sich eine neue Mail zustellen.');
      }
      this.api.sendRequest(StartSessionByLinkRequest, {
        sessionId,
        loginByLinkSecret
      }).subscribe({
        next: result => {
          this.facade.refreshSession().pipe(take(1)).subscribe(() => {
            this.router.navigate(['/']);
          });
        },
        error: err => {
          this.messages.add(err);
          this.isLoading.set(false);
        }
      });
    }
    static {
      this.ɵfac = function CoreBeginSessionByUrlComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || CoreBeginSessionByUrlComponent)(i0.ɵɵdirectiveInject(i1.ActivatedRoute), i0.ɵɵdirectiveInject(i1.Router), i0.ɵɵdirectiveInject(i2.AccessFacade), i0.ɵɵdirectiveInject(i3.Store), i0.ɵɵdirectiveInject(i2.LocalMessageHandlerService), i0.ɵɵdirectiveInject(i2.AppRequestService));
      };
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: CoreBeginSessionByUrlComponent,
        selectors: [["core-begin-session-by-url"]],
        features: [i0.ɵɵProvidersFeature([LocalMessageHandlerService])],
        decls: 4,
        vars: 1,
        consts: [["size", "small"], ["uiGrid", "", 1, "p-align-center"], ["uiButton", "", "label", "Zur\u00FCck zur Startseite", "routerLink", "/"], [1, "p-col-3", 2, "text-align", "right"], ["icon", "circle-notch", "size", "4x", 1, "ui-float-right", "color-primary", 3, "spin"], [1, "p-col-9"]],
        template: function CoreBeginSessionByUrlComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵelementStart(0, "ui-content-centered")(1, "ui-card", 0);
            i0.ɵɵconditionalCreate(2, CoreBeginSessionByUrlComponent_Conditional_2_Template, 4, 2, "div", 1)(3, CoreBeginSessionByUrlComponent_Conditional_3_Template, 3, 0);
            i0.ɵɵelementEnd()();
          }
          if (rf & 2) {
            i0.ɵɵadvance(2);
            i0.ɵɵconditional(ctx.isFrontend ? 2 : 3);
          }
        },
        dependencies: [UiContentCenteredComponent, i4.UiCardComponent, UiIconsModule, i5.UiIconComponent, UiButtonComponent, AppMessagesComponent, RouterModule, i1.RouterLink],
        encapsulation: 2
      });
    }
  }
  return CoreBeginSessionByUrlComponent;
})();